import { useState } from "react";
import { toast } from "sonner";
import { PencilSimpleIcon, CheckIcon, XIcon } from "@phosphor-icons/react";

import {
  INPUT_CLS,
  PrimaryButton,
  SecondaryButton,
} from "../crm/Form";
import { DAYS_OF_WEEK, updateTeamMember } from "../../lib/team";
import { formatApiError } from "../../lib/api";

const FIELDS = [
  { key: "preferred_start_time", label: "Start time", type: "time" },
  { key: "preferred_end_time", label: "End time", type: "time" },
  { key: "max_jobs_per_day", label: "Max jobs per day", type: "number", min: 1, max: 20 },
  { key: "travel_radius_miles", label: "Travel radius (mi)", type: "number", min: 0, max: 500 },
];

export default function SchedulingPrefsCard({ member, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => normalize(member.scheduling_preferences));
  const [busy, setBusy] = useState(false);

  const begin = () => {
    setDraft(normalize(member.scheduling_preferences));
    setEditing(true);
  };
  const cancel = () => setEditing(false);

  const toggleDay = (d) => {
    setDraft((cur) => {
      const has = cur.days_available.includes(d);
      return {
        ...cur,
        days_available: has
          ? cur.days_available.filter((x) => x !== d)
          : [...cur.days_available, d],
      };
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        scheduling_preferences: serialize(draft),
      };
      await updateTeamMember(member.id, payload);
      toast.success("Scheduling preferences saved");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sched = member.scheduling_preferences || {};

  return (
    <section
      data-testid="team-sched-card"
      className="bg-white border border-slate-200 rounded-md p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading text-sm font-semibold text-slate-900">
          Scheduling preferences
        </div>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={begin}
            data-testid="team-sched-edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Edit scheduling preferences"
          >
            <PencilSimpleIcon size={14} />
          </button>
        )}
      </div>

      {!editing && (
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {FIELDS.map((f) => (
            <Row
              key={f.key}
              label={f.label}
              value={displayValue(sched[f.key], f.type)}
              testid={`team-sched-${f.key}`}
            />
          ))}
          <div className="sm:col-span-2">
            <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
              Days available
            </dt>
            <dd className="mt-1 flex flex-wrap gap-1.5">
              {DAYS_OF_WEEK.map((d) => {
                const active = (sched.days_available || []).includes(d.value);
                return (
                  <span
                    key={d.value}
                    data-testid={`team-sched-day-${d.value}`}
                    className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-400 border-slate-200"
                    }`}
                  >
                    {d.short}
                  </span>
                );
              })}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
              Notes
            </dt>
            <dd
              data-testid="team-sched-notes"
              className="mt-0.5 text-sm text-slate-900 whitespace-pre-wrap"
            >
              {sched.scheduling_notes || (
                <span className="text-slate-400">—</span>
              )}
            </dd>
          </div>
        </dl>
      )}

      {editing && (
        <div className="space-y-4" data-testid="team-sched-editor">
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <FieldRow
                key={f.key}
                label={f.label}
                type={f.type}
                value={draft[f.key]}
                min={f.min}
                max={f.max}
                onChange={(v) =>
                  setDraft((c) => ({ ...c, [f.key]: v }))
                }
                testid={`team-sched-${f.key}-input`}
              />
            ))}
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-1.5">
              Days available
            </label>
            <div className="flex flex-wrap gap-1.5">
              {DAYS_OF_WEEK.map((d) => {
                const active = draft.days_available.includes(d.value);
                return (
                  <button
                    type="button"
                    key={d.value}
                    onClick={() => toggleDay(d.value)}
                    data-testid={`team-sched-day-toggle-${d.value}`}
                    className={`text-xs px-3 py-1 rounded-md border transition ${
                      active
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    {d.short}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-1.5">
              Scheduling notes
            </label>
            <textarea
              rows={3}
              maxLength={500}
              value={draft.scheduling_notes || ""}
              onChange={(e) =>
                setDraft((c) => ({ ...c, scheduling_notes: e.target.value }))
              }
              data-testid="team-sched-notes-input"
              className={INPUT_CLS}
            />
            <div className="text-[10px] text-slate-500 mt-1">
              {(draft.scheduling_notes || "").length}/500
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <SecondaryButton type="button" onClick={cancel} disabled={busy}>
              <XIcon size={14} /> Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={save}
              disabled={busy}
              data-testid="team-sched-save"
            >
              <CheckIcon size={14} /> {busy ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </section>
  );
}

function Row({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <dt className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-slate-900">
        {value || <span className="text-slate-400">—</span>}
      </dd>
    </div>
  );
}

function FieldRow({ label, type, value, onChange, min, max, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        min={min}
        max={max}
        onChange={(e) => {
          const v = e.target.value;
          if (type === "number") {
            onChange(v === "" ? null : Number(v));
          } else {
            onChange(v || null);
          }
        }}
        data-testid={testid}
        className={INPUT_CLS}
      />
    </div>
  );
}

function displayValue(v, type) {
  if (v === null || v === undefined || v === "") return null;
  return String(v);
}

function normalize(s) {
  s = s || {};
  return {
    preferred_start_time: s.preferred_start_time || null,
    preferred_end_time: s.preferred_end_time || null,
    max_jobs_per_day: s.max_jobs_per_day ?? null,
    travel_radius_miles: s.travel_radius_miles ?? null,
    days_available: Array.isArray(s.days_available) ? [...s.days_available] : [],
    scheduling_notes: s.scheduling_notes || "",
  };
}

function serialize(draft) {
  return {
    preferred_start_time: draft.preferred_start_time || null,
    preferred_end_time: draft.preferred_end_time || null,
    max_jobs_per_day: draft.max_jobs_per_day ?? null,
    travel_radius_miles: draft.travel_radius_miles ?? null,
    days_available: draft.days_available || [],
    scheduling_notes: (draft.scheduling_notes || "").trim() || null,
  };
}
